import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { videoLibraryApi } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Loader2, Plus, Trash2, EyeOff, Eye, Youtube } from 'lucide-react';
import LanguageFlag from '../components/LanguageFlag';

const LANGUAGE_OPTIONS = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'it', name: 'Italian' },
  { code: 'ru', name: 'Russian' },
  { code: 'zh', name: 'Mandarin' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
  { code: 'ar', name: 'Arabic' },
  { code: 'fa', name: 'Persian' },
  { code: 'he', name: 'Hebrew' },
  { code: 'nl', name: 'Dutch' },
];

const LEVEL_OPTIONS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

export default function AdminFeaturedVideos() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  // Add form
  const [newUrl, setNewUrl] = useState('');
  const [newLang, setNewLang] = useState('zh');
  const [newLevel, setNewLevel] = useState('A2');
  const [newTopic, setNewTopic] = useState('');
  const [adding, setAdding] = useState(false);

  // Bulk paste form
  const [bulkText, setBulkText] = useState('');
  const [bulkLang, setBulkLang] = useState('zh');
  const [bulkLevel, setBulkLevel] = useState('A2');
  const [bulkTopic, setBulkTopic] = useState('');
  const [bulkSeeding, setBulkSeeding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await videoLibraryApi.adminListAllFeatured();
      setRows(res.data?.videos || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load featured videos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleAdd = async (e) => {
    e?.preventDefault?.();
    if (!newUrl.trim()) {
      toast.error('YouTube URL is required');
      return;
    }
    setAdding(true);
    try {
      const res = await videoLibraryApi.seedFeatured([
        {
          url: newUrl.trim(),
          language_code: newLang,
          level: newLevel,
          topic: newTopic.trim(),
        },
      ]);
      const { inserted = 0, updated = 0, errors = [] } = res.data || {};
      if (errors.length) {
        toast.error(`Error: ${errors[0].error || 'unknown'}`);
      } else if (inserted) {
        toast.success('Video added to featured');
      } else if (updated) {
        toast.success('Video updated');
      }
      setNewUrl('');
      setNewTopic('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Permanently delete "${row.title}" (${row.language_code}/${row.level})?`)) return;
    try {
      await videoLibraryApi.deleteFeatured(row.external_id, row.language_code);
      toast.success('Deleted');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleBulkSeed = async (e) => {
    e?.preventDefault?.();
    const text = bulkText.trim();
    if (!text) {
      toast.error('Paste at least one URL or a JSON array of seeds');
      return;
    }

    // Accept either a JSON array, or one entry per line (URL, or "URL | lang | level | topic")
    let seeds = [];
    if (text.startsWith('[') || text.startsWith('{')) {
      try {
        const parsed = JSON.parse(text);
        seeds = Array.isArray(parsed) ? parsed : [parsed];
      } catch (err) {
        toast.error('Invalid JSON');
        return;
      }
    } else {
      seeds = text
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
          const parts = line.split('|').map((p) => p.trim());
          return {
            url: parts[0],
            language_code: parts[1] || bulkLang,
            level: parts[2] || bulkLevel,
            topic: parts[3] || bulkTopic || undefined,
          };
        });
    }

    // Fill defaults from the form dropdowns
    seeds = seeds.map((s) => ({
      url: s.url,
      language_code: s.language_code || bulkLang,
      level: s.level || bulkLevel,
      topic: s.topic || bulkTopic || undefined,
    }));

    const valid = seeds.filter((s) => s.url && /https?:\/\//.test(s.url));
    if (valid.length === 0) {
      toast.error('No valid URLs found');
      return;
    }

    setBulkSeeding(true);
    try {
      const res = await videoLibraryApi.seedFeatured(valid);
      const { inserted = 0, updated = 0, skipped = 0, errors = [] } = res.data || {};
      toast.success(
        `Seed complete — inserted ${inserted}, updated ${updated}, skipped ${skipped}${errors.length ? `, ${errors.length} error(s)` : ''}`
      );
      if (errors.length) {
        console.warn('Seed errors:', errors);
      }
      setBulkText('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk seed failed');
    } finally {
      setBulkSeeding(false);
    }
  };

  const handleToggleHidden = async (row) => {
    try {
      if (row.hidden) {
        await videoLibraryApi.unhideFeatured(row.external_id);
        toast.success('Video unhidden');
      } else {
        await videoLibraryApi.hideFeatured(row.external_id);
        toast.success('Video hidden');
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  const filtered = rows.filter((r) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      (r.title || '').toLowerCase().includes(q) ||
      (r.language_code || '').toLowerCase().includes(q) ||
      (r.level || '').toLowerCase().includes(q) ||
      (r.channel || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="admin-featured-videos">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Youtube className="h-6 w-6 text-red-500" />
          Featured Videos
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Curated YouTube videos that appear in every user's Video Library. Changes are global.
        </p>
      </div>

      {/* Add form */}
      <form
        onSubmit={handleAdd}
        className="rounded-lg border p-4 space-y-3 bg-muted/30"
        data-testid="admin-featured-add-form"
      >
        <h2 className="font-semibold text-sm flex items-center gap-2">
          <Plus className="h-4 w-4" /> Add a featured video
        </h2>
        <div className="grid gap-2 sm:grid-cols-[1fr_130px_110px_1fr_auto]">
          <Input
            placeholder="https://youtube.com/watch?v=…"
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            data-testid="admin-featured-url"
          />
          <Select value={newLang} onValueChange={setNewLang}>
            <SelectTrigger data-testid="admin-featured-lang"><SelectValue /></SelectTrigger>
            <SelectContent>
              {LANGUAGE_OPTIONS.map((l) => (
                <SelectItem key={l.code} value={l.code}>{l.name} ({l.code})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={newLevel} onValueChange={setNewLevel}>
            <SelectTrigger data-testid="admin-featured-level"><SelectValue /></SelectTrigger>
            <SelectContent>
              {LEVEL_OPTIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input
            placeholder="Topic (optional)"
            value={newTopic}
            onChange={(e) => setNewTopic(e.target.value)}
            data-testid="admin-featured-topic"
          />
          <Button type="submit" disabled={adding} data-testid="admin-featured-add-submit">
            {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
            Add
          </Button>
        </div>
      </form>

      {/* Bulk paste form */}
      <form
        onSubmit={handleBulkSeed}
        className="rounded-lg border p-4 space-y-3 bg-muted/30"
        data-testid="admin-featured-bulk-form"
      >
        <h2 className="font-semibold text-sm flex items-center gap-2">
          <Plus className="h-4 w-4" /> Bulk-seed videos
        </h2>
        <p className="text-xs text-muted-foreground">
          Paste one URL per line (uses the defaults below), or paste a JSON array like{' '}
          <code className="px-1 bg-muted rounded">{'[{"url":"…","language_code":"es","level":"A1","topic":"Greetings"}]'}</code>.
          You can also override per-line with <code className="px-1 bg-muted rounded">URL | lang | level | topic</code>.
        </p>
        <textarea
          value={bulkText}
          onChange={(e) => setBulkText(e.target.value)}
          placeholder={
            "https://www.youtube.com/watch?v=abc123\n" +
            "https://www.youtube.com/watch?v=def456 | pt | A2 | Travel\n" +
            "— or —\n" +
            '[{"url":"https://www.youtube.com/watch?v=xyz","language_code":"fr","level":"B1"}]'
          }
          rows={6}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-primary"
          data-testid="admin-featured-bulk-text"
        />
        <div className="grid gap-2 sm:grid-cols-[130px_110px_1fr_auto]">
          <Select value={bulkLang} onValueChange={setBulkLang}>
            <SelectTrigger data-testid="admin-featured-bulk-lang"><SelectValue /></SelectTrigger>
            <SelectContent>
              {LANGUAGE_OPTIONS.map((l) => (
                <SelectItem key={l.code} value={l.code}>{l.name} ({l.code})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={bulkLevel} onValueChange={setBulkLevel}>
            <SelectTrigger data-testid="admin-featured-bulk-level"><SelectValue /></SelectTrigger>
            <SelectContent>
              {LEVEL_OPTIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input
            placeholder="Default topic (optional)"
            value={bulkTopic}
            onChange={(e) => setBulkTopic(e.target.value)}
            data-testid="admin-featured-bulk-topic"
          />
          <Button type="submit" disabled={bulkSeeding} data-testid="admin-featured-bulk-submit">
            {bulkSeeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
            Seed batch
          </Button>
        </div>
      </form>

      {/* Filter + list */}
      <div className="flex items-center gap-3">
        <Input
          placeholder="Filter by title, language, level, channel…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="max-w-sm"
          data-testid="admin-featured-filter"
        />
        <span className="text-xs text-muted-foreground" data-testid="admin-featured-count">
          {loading ? 'Loading…' : `${filtered.length} / ${rows.length} videos`}
        </span>
      </div>

      <div className="rounded-lg border divide-y">
        {loading && (
          <div className="p-6 flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="p-6 text-center text-sm text-muted-foreground">No featured videos yet.</div>
        )}
        {!loading && filtered.map((row) => (
          <div
            key={`${row.external_id}-${row.language_code}`}
            className={`flex items-center gap-3 p-3 ${row.hidden ? 'opacity-50' : ''}`}
            data-testid={`admin-featured-row-${row.external_id}-${row.language_code}`}
          >
            {row.thumbnail_url && (
              <img
                src={row.thumbnail_url}
                alt=""
                className="w-24 h-16 object-cover rounded shrink-0 bg-muted"
                loading="lazy"
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{row.title || row.external_id}</div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                <LanguageFlag code={row.language_code} size="sm" />
                <span>{row.language_code.toUpperCase()}</span>
                <span className="px-1.5 py-0.5 rounded bg-muted text-[10px] tabular-nums">{row.level}</span>
                {row.topic && <span>· {row.topic}</span>}
                {row.channel && <span>· {row.channel}</span>}
                {row.has_subs_preflight === false && (
                  <span className="text-amber-600 dark:text-amber-400">· no-captions</span>
                )}
                {row.hidden && <span className="text-red-500">· HIDDEN</span>}
              </div>
            </div>
            <a
              href={`https://www.youtube.com/watch?v=${row.external_id}`}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-muted-foreground hover:underline shrink-0"
            >
              open ↗
            </a>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleToggleHidden(row)}
              title={row.hidden ? 'Un-hide' : 'Hide'}
              data-testid={`admin-featured-toggle-${row.external_id}`}
            >
              {row.hidden ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="text-red-500 hover:text-red-600"
              onClick={() => handleDelete(row)}
              title="Delete permanently"
              data-testid={`admin-featured-delete-${row.external_id}-${row.language_code}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
